import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Form as FormikForm, Formik } from 'formik';
import { MutationFunction } from 'react-apollo';
import moment from 'moment';

import { LoadMoreButton, Queries } from 'components';

import SearchForm from '../components/Reports/SearchForm';
import ResultsTable from '../components/Reports/ResultsTable';
import {
  CreateReportRequestComponent,
  GetReportRequestsDocument,
  Report,
  RunReportInput,
} from 'api';
import { useNotify } from 'hooks';

import { ReportsSchema } from '../validations';
import { createReportRequest } from 'apollo/reducers';

const START_AT = moment()
  .subtract(8, 'd')
  .startOf('d')
  .unix();
const END_AT = moment()
  .subtract(1, 'd')
  .startOf('d')
  .unix();
const REPORT_REQUEST_PAGE_SIZE = 20;

const Reports: FC = () => {
  const [working, setWorking] = useState(false);
  const { t } = useTranslation();
  const notify = useNotify();

  function handleError() {
    setWorking(false);
    notify.mutationError();
  }

  function handleCompleted() {
    setWorking(false);
    notify.info({ message: t('reports:reportRequested') });
  }

  function handleSubmit(mutation: MutationFunction, data: RunReportInput) {
    setWorking(true);

    return mutation({
      variables: { data },
      update: (cache, result) => {
        createReportRequest({
          cache,
          result,
          query: GetReportRequestsDocument,
          variables: { data: { limit: REPORT_REQUEST_PAGE_SIZE } },
        });
      },
    });
  }

  function renderMutation(reports: Report[]) {
    const initialValues = {
      report_name: reports[0].name,
      start_at: START_AT,
      end_at: END_AT,
    };

    return (
      <Fragment>
        <CreateReportRequestComponent onError={handleError} onCompleted={handleCompleted}>
          {mutationFn => (
            <Formik
              isInitialValid
              initialValues={initialValues}
              onSubmit={values => handleSubmit(mutationFn, values)}
              validationSchema={ReportsSchema}
              render={() => (
                <FormikForm>
                  <SearchForm reports={reports} working={working} />
                </FormikForm>
              )}
            />
          )}
        </CreateReportRequestComponent>
      </Fragment>
    );
  }

  return (
    <Queries.GetReports>
      {reports => (
        <Queries.GetReportRequests limit={REPORT_REQUEST_PAGE_SIZE}>
          {(requests, count, loading, onLoadMore) => (
            <Fragment>
              {renderMutation(reports)}
              <ResultsTable requests={requests} count={count} />
              {!!onLoadMore && <LoadMoreButton loading={loading} onClick={onLoadMore} />}
            </Fragment>
          )}
        </Queries.GetReportRequests>
      )}
    </Queries.GetReports>
  );
};

export default Reports;
